import { SvgIcon } from '@mui/material'
import {
  IconClockQuestion,
  IconBuilding,
  IconLayoutCollage,
  IconListTree,
  IconTable,
  IconTimeline,
  IconShoppingBag,
  ReactNode,
} from '@tabler/icons-react'
import { getProductDetailSlug, GetProductDetailSlugParams } from './utils'
import { AppRole } from '../auth/useRole'

export const eShopDomain = 'https://wollem.com'
export const getEshopLink = {
  productDetail: (params: GetProductDetailSlugParams) =>
    `${eShopDomain}/product-details/${getProductDetailSlug(params)}`,
}

export const EDIT_VISITOR_ROUTE_PARAM = 'id' as const
export const editVisitorRoute = () => `/visitors/edit/:${EDIT_VISITOR_ROUTE_PARAM}` as const
export const editVisitorPath = (id: number) => `/visitors/edit/${id}` as const

const width = 18
const height = 18

type RouteData = {
  key: string
  route: string
  label: string
  Icon: ReactNode
  inSidebar: boolean
  menu: Record<string, Omit<RouteData, 'menu'>> | null
  allow: AppRole[]
}

export const routes = {
  home: {
    key: 'home',
    route: '/',
    label: 'Dashboard',
    Icon: (
      <SvgIcon sx={{ width, height }}>
        <IconLayoutCollage />
      </SvgIcon>
    ),
    inSidebar: true,
    menu: null,
    allow: ['admin', 'marketing', 'moderator'],
  },
  createVisitor: {
    key: 'createVisitor',
    route: '/visitors/create',
    label: 'Create Visitor',
    Icon: null,
    inSidebar: false,
    menu: null,
    allow: ['admin', 'moderator'],
  },
  editVisitor: {
    key: 'editVisitor',
    route: '/visitors/edit/:id',
    label: 'Edit Visitor',
    Icon: null,
    inSidebar: false,
    menu: null,
    allow: ['admin', 'moderator'],
  },
  customers: {
    key: 'customers',
    route: '',
    label: 'Customers',
    Icon: null,
    inSidebar: true,
    allow: ['admin', 'moderator'],
    menu: {
      customersList: {
        key: 'customerList',
        route: '/customers/list',
        label: 'Customer List',
        Icon: (
          <SvgIcon sx={{ width, height }}>
            <IconTable />
          </SvgIcon>
        ),
        allow: ['admin', 'moderator'],
        inSidebar: true,
      },
      customerRequests: {
        key: 'customerRequests',
        route: '/customers/requests',
        label: 'Customer Requests',
        Icon: (
          <SvgIcon sx={{ width, height }}>
            <IconClockQuestion />
          </SvgIcon>
        ),
        allow: ['admin', 'moderator'],
        inSidebar: true,
      },
    },
  },
  createAgency: {
    key: 'createAgency',
    route: '/sales/agencies/create',
    label: 'Create Agency',
    Icon: null,
    inSidebar: false,
    allow: ['admin'],
    menu: null,
  },
  sales: {
    key: 'sales',
    route: '',
    label: 'Sales',
    Icon: null,
    inSidebar: true,
    allow: ['admin', 'moderator'],
    menu: {
      visitors: {
        key: 'visitors',
        route: '/sales/visitors',
        label: 'Visitors',
        Icon: (
          <SvgIcon sx={{ width, height }}>
            <IconListTree />
          </SvgIcon>
        ),
        inSidebar: true,
        allow: ['admin', 'moderator'],
      },
      orders: {
        key: 'orders',
        route: '/sales/shopify_orders',
        label: 'Shopify Orders',
        Icon: (
          <SvgIcon sx={{ width, height }}>
            <IconShoppingBag />
          </SvgIcon>
        ),
        inSidebar: true,
        allow: ['admin', 'moderator'],
      },
      reports: {
        key: 'reports',
        route: '/sales/reports',
        label: 'Reports',
        Icon: (
          <SvgIcon sx={{ width, height }}>
            <IconTimeline />
          </SvgIcon>
        ),
        inSidebar: true,
        allow: ['admin', 'moderator', 'marketing'],
      },
      agencies: {
        key: 'agencies',
        route: '/sales/agencies',
        label: 'Agencies',
        Icon: (
          <SvgIcon sx={{ width, height }}>
            <IconBuilding />
          </SvgIcon>
        ),
        inSidebar: true,
        allow: ['admin'],
      },
    },
  },
  models: {
    key: 'models',
    route: '',
    label: 'Models',
    Icon: null,
    inSidebar: true,
    allow: ['admin', 'moderator'],
    menu: {
      list: {
        key: 'list',
        inSidebar: true,
        route: '/models/list',
        label: 'Models List',
        Icon: (
          <SvgIcon sx={{ width, height }}>
            <IconTable />
          </SvgIcon>
        ),
        allow: ['admin', 'moderator'],
      },
    },
  },
  inventory: {
    key: 'inventory',
    route: '',
    label: 'Inventory',
    Icon: null,
    inSidebar: true,
    allow: ['admin', 'moderator'],
    menu: {
      wollemInventory: {
        key: 'wollemInventory',
        inSidebar: true,
        route: '/inventory/wollem',
        label: 'Wollem',
        Icon: (
          <SvgIcon sx={{ width, height }}>
            <IconTable />
          </SvgIcon>
        ),
        allow: ['admin', 'moderator'],
      },
      storyousInventory: {
        key: 'storyousInventory',
        inSidebar: true,
        route: '/inventory/storyous',
        label: 'Storyous',
        Icon: (
          <SvgIcon sx={{ width, height }}>
            <IconTable />
          </SvgIcon>
        ),
        allow: ['admin', 'moderator'],
      },
    },
  },
} as const satisfies Record<string, RouteData>

export const readSlugAsScalar = (slug: string | string[]) => (Array.isArray(slug) ? slug[0] : slug)
